import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Bitcoin, ExternalLink, Clock, AlertCircle, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RedirectPage = () => {
  const { shortCode } = useParams();
  const navigate = useNavigate();
  const [linkData, setLinkData] = useState(null);
  const [countdown, setCountdown] = useState(5);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [redirecting, setRedirecting] = useState(false);

  const fetchLinkData = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/r/${shortCode}`);
      setLinkData(response.data);
    } catch (err) {
      setError('Link not found or has been deactivated');
    } finally {
      setLoading(false);
    }
  }, [shortCode]);

  useEffect(() => {
    fetchLinkData();
  }, [fetchLinkData]);

  useEffect(() => {
    if (!linkData || countdown <= 0) return;

    const timer = setInterval(() => {
      setCountdown((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [linkData, countdown]);

  const handleRedirect = useCallback(async () => {
    if (redirecting) return;
    setRedirecting(true);

    try {
      // Record the click
      await axios.post(`${API}/r/${shortCode}/click`);
      // Redirect to original URL
      window.location.href = linkData.original_url;
    } catch (err) {
      // Still redirect even if tracking fails
      window.location.href = linkData.original_url;
    }
  }, [redirecting, shortCode, linkData]);

  useEffect(() => {
    if (countdown === 0 && linkData && !redirecting) {
      handleRedirect();
    }
  }, [countdown, linkData, redirecting, handleRedirect]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#F7931A] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Loading link...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="card-dark p-8 sm:p-12 max-w-md w-full text-center">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-6" />
          <h1 className="font-heading text-2xl font-bold mb-4">Link Not Found</h1>
          <p className="text-slate-400 mb-6">{error}</p>
          <Button onClick={() => navigate('/')} className="btn-gold">
            Go to Homepage
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-[#07080D]">
      <div className="card-dark p-8 sm:p-12 max-w-lg w-full text-center relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#F7931A]/5 to-transparent" />

        <div className="relative">
          {/* Bitcoin Icon */}
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-[#F7931A]/10 border border-[#F7931A]/30 mb-6">
            <Bitcoin className="w-10 h-10 text-[#F7931A]" />
          </div>

          {/* Title */}
          <h1 className="font-heading text-2xl sm:text-3xl font-bold mb-4">
            SatoshiShort
          </h1>

          {/* Link Info */}
          <div className="mb-8">
            <p className="text-slate-400 mb-2">You are being redirected to:</p>
            <p className="font-mono text-sm text-[#F7931A] bg-[#F7931A]/10 px-4 py-2 rounded-lg truncate">
              {linkData?.original_url}
            </p>
          </div>

          {/* Countdown Timer */}
          <div className="mb-8">
            <div className="inline-flex items-center justify-center w-24 h-24 rounded-full border-4 border-[#F7931A] bg-[#F7931A]/10 mb-4">
              <span
                className="font-mono text-4xl font-bold text-[#F7931A]"
                data-testid="redirect-countdown"
              >
                {countdown}
              </span>
            </div>
            <p className="text-slate-400 flex items-center justify-center gap-2">
              <Clock className="w-4 h-4" />
              Redirecting in {countdown} seconds...
            </p>
          </div>

          {/* Skip Button */}
          <Button
            onClick={handleRedirect}
            disabled={redirecting}
            className="btn-gold px-8 py-5"
            data-testid="redirect-continue-btn"
          >
            {redirecting ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <ExternalLink className="w-5 h-5 mr-2" />
                Continue Now
              </>
            )}
          </Button>

          {/* Footer */}
          <p className="text-xs text-slate-500 mt-8">
            Powered by SatoshiShort • Earn Bitcoin with every link
          </p>
        </div>
      </div>
    </div>
  );
};

export default RedirectPage;
